import { useEffect, useState } from "react";
import { API, axios } from "@/api";
import PanelRefreshButton from "./PanelRefreshButton";
import { Loader2, AlertTriangle, CheckCircle2, Info, Zap } from "lucide-react";

// v1.0.0 Batch — Source Health panel
// Shows every source across both cascades with its current cascade option,
// credits used/limit, % used, and status dot. Pulls /api/admin/source-health.

const STATUS_DOT = {
  green:  "bg-emerald-500",
  blue:   "bg-sky-500",
  orange: "bg-orange-500",
  red:    "bg-red-500 animate-pulse",
  gray:   "bg-slate-300",
};

const CASCADE_LABEL = {
  discovery:        "Discovery",
  validation:       "Validation",
  enrichment:       "Enrichment",
  google_validation:"Google Validation",
  gap_fill:         "Gap Fill",
};

export default function SourceHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [anomalies, setAnomalies] = useState([]);

  const load = async () => {
    try {
      const [res, anom] = await Promise.all([
        axios.get(`${API}/admin/source-health`),
        axios.get(`${API}/admin/sweep-anomalies`).catch(() => ({ data: { anomalies: [] } })),
      ]);
      setData(res.data);
      setAnomalies(anom.data?.anomalies || []);
      setLastRefreshedAt(Date.now());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  const alerts = data.alerts || [];
  const sources = data.sources || [];

  // Group sources by cascade for a cleaner table layout
  const grouped = {};
  for (const s of sources) {
    grouped[s.cascade] = grouped[s.cascade] || [];
    grouped[s.cascade].push(s);
  }

  return (
    <div className="space-y-3" data-testid="source-health-panel">
      <div className="flex justify-end -mb-2">
        <PanelRefreshButton onRefresh={load} lastRefreshedAt={lastRefreshedAt} testId="source-health-refresh" />
      </div>

      {/* v1.0.2 Item 1 — Sweep Anomaly Banner (volume drop >40% vs 14d baseline) */}
      {anomalies.length > 0 && (
        <div className="rounded-xl border-2 border-red-300 bg-gradient-to-r from-red-50 to-orange-50 p-3" data-testid="sweep-anomaly-banner">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-red-600 animate-pulse" />
            <p className="text-[11px] font-black uppercase tracking-wider text-red-800">
              {anomalies.length} source{anomalies.length > 1 ? "s" : ""} dropped overnight
            </p>
          </div>
          <div className="space-y-1">
            {anomalies.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`sweep-anomaly-row-${a.source_name}`}>
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="font-bold text-slate-700">{a.source_name}</span>
                <span className="text-slate-500">→</span>
                <span className="text-slate-700">
                  −{a.drop_pct}% vs {a.baseline_nights}-night baseline
                  <span className="text-slate-400 ml-1">
                    (volume {a.last_night_volume} vs avg {a.baseline_avg})
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerts banner — red/orange cascades */}
      {alerts.length > 0 && (
        <div className="rounded-xl border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-red-50 p-3" data-testid="source-health-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <p className="text-[11px] font-black uppercase tracking-wider text-orange-800">
              {alerts.length} cascade{alerts.length > 1 ? "s" : ""} need{alerts.length > 1 ? "" : "s"} review
            </p>
          </div>
          <div className="space-y-1">
            {alerts.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`source-health-alert-${i}`}>
                <span className={`w-2 h-2 rounded-full ${STATUS_DOT[a.status]}`} />
                <span className="font-bold text-slate-700">{CASCADE_LABEL[a.cascade] || a.cascade} / {a.data_type}</span>
                <span className="text-slate-500">→</span>
                <span className="text-slate-700">
                  {a.status === "red"
                    ? <>All sources exhausted · cascade blocked</>
                    : <>Running on Option {a.active_position || "?"} · review efficiency</>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overall indicator */}
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50">
        <span className={`w-3 h-3 rounded-full ${STATUS_DOT[data.overall]}`} data-testid="source-health-overall-dot" />
        <p className="text-sm font-bold text-slate-700">
          Overall: <span className="capitalize">{data.overall}</span>
        </p>
        <p className="text-[10px] text-slate-400 ml-auto">{sources.length} sources tracked across {Object.keys(grouped).length} cascades</p>
      </div>

      {/* Per-cascade source tables */}
      {Object.entries(grouped).map(([cascade, cascadeSources]) => (
        <div key={cascade} className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
              {CASCADE_LABEL[cascade] || cascade} cascade
              {cascadeSources[0]?.adaptive && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">adaptive</span>}
            </p>
          </div>
          <table className="w-full text-[11px]" data-testid={`source-health-table-${cascade}`}>
            <thead>
              <tr className="bg-white text-slate-400 uppercase tracking-wider text-[9px]">
                <th className="px-3 py-1.5 text-left font-bold">Source</th>
                <th className="px-2 py-1.5 text-left font-bold">Data Type</th>
                <th className="px-2 py-1.5 text-center font-bold">Option</th>
                <th className="px-2 py-1.5 text-right font-bold">Used</th>
                <th className="px-2 py-1.5 text-right font-bold">Limit</th>
                <th className="px-2 py-1.5 text-right font-bold">% Used</th>
                <th className="px-2 py-1.5 text-center font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {cascadeSources.map((s) => (
                <tr key={s.name} className="border-t border-slate-100" data-testid={`source-health-row-${s.name}`}>
                  <td className="px-3 py-2 font-bold text-slate-700">
                    {s.name}
                    {s.near_exhausted && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                        <Info className="w-2.5 h-2.5" />
                        near limit
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-slate-500">{s.data_type}</td>
                  <td className="px-2 py-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                      s.position === "A" ? "bg-emerald-100 text-emerald-700" :
                      s.position === "B" ? "bg-sky-100 text-sky-700" :
                      s.position === "C" || s.position === "last" ? "bg-orange-100 text-orange-700" :
                      "bg-slate-100 text-slate-500"
                    }`}>
                      {s.position}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">{s.credits_used}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-500">
                    {s.unlimited ? "∞" : s.limit ?? "?"}
                    {s.needs_investigation && <span className="ml-1 text-amber-600 text-[9px]">⚠</span>}
                  </td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">
                    {s.pct_used != null ? `${s.pct_used}%` : "—"}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_DOT[s.source_status] || STATUS_DOT.gray}`}
                          data-testid={`source-health-dot-${s.name}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <p className="text-[10px] text-slate-400 text-center pt-1">
        <CheckCircle2 className="inline w-3 h-3 mr-1" />
        Cascade position A active → green · B active → blue · C/last active → orange · all exhausted → red
      </p>
    </div>
  );
}
